import { type AnalyticsEvent } from '@autone/analytics';
import { useMemo, useState } from 'react';
import { type Control, type FieldValues, type Path } from 'react-hook-form';

import { type GetCoreLocationCountriesRequest } from '../../../redux/services';
import { useGetCoreLocationCountriesQuery } from '../../../redux/services/core';
import { TailwindFormField } from '../../../tailwind';
import { MultiSelectDropdown } from '../../multi-select-dropdown/MultiSelectDropdown';
import { useMultiselectApiFilters } from '../hooks';
import { type GetMultiselectMapper } from '../types';
import {
  CLIENT_SIDE_SEARCH_DROPDOWN_LIMIT,
  mapIdsToScopeItem,
} from '../utilities';

type CountriesFilters = GetCoreLocationCountriesRequest['body']['filters'];

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  locationsMap?: GetMultiselectMapper<CountriesFilters['geographic'], T>;
  locationClustersMap?: GetMultiselectMapper<
    CountriesFilters['location_cluster'],
    T
  >;
  disabled?: boolean;
  analyticsEvent?: AnalyticsEvent;
}

export const CoreLocationCountriesMultiselect = <T extends FieldValues>({
  control,
  name,
  locationsMap,
  locationClustersMap,
  disabled,
  analyticsEvent,
}: Props<T>) => {
  const geographicFilters = useMultiselectApiFilters({
    control,
    map: locationsMap,
    relevantFilterKeys: [
      'regions',
      'locations',
      'location_types',
      'territories',
    ],
  });
  const locationClustersFilters = useMultiselectApiFilters({
    control,
    map: locationClustersMap,
    relevantFilterKeys: ['location_clusters'],
  });

  const [searchTerm, setSearchTerm] = useState('');
  const { data, isLoading } = useGetCoreLocationCountriesQuery({
    query: { limit: CLIENT_SIDE_SEARCH_DROPDOWN_LIMIT },
    body: {
      filters: {
        geographic: geographicFilters,
        location_cluster: locationClustersFilters,
        location_groups: {},
      },
    },
  });

  const options = useMemo(
    () =>
      data?.items.filter((option) =>
        option.description.toLowerCase().includes(searchTerm.toLowerCase()),
      ) ?? [],
    [data, searchTerm],
  );
  const optionsMap = useMemo(
    () => new Map(options.map((option) => [option.id, option])),
    [options],
  );

  return (
    <TailwindFormField
      control={control}
      name={name}
      render={({ field: { value, onChange, disabled: fieldDisabled } }) => (
        <MultiSelectDropdown
          className="h-fit"
          loading={isLoading}
          options={options}
          getOptionId={(option) => option.id}
          getOptionLabel={(option) => option?.description}
          onSearchTermChange={setSearchTerm}
          // @ts-expect-error
          selected={value?.map((option) => option.id) ?? []}
          onSelectedChange={(ids) =>
            onChange(mapIdsToScopeItem(optionsMap)(ids))
          }
          disabled={disabled || fieldDisabled}
          analyticsEvent={analyticsEvent}
        />
      )}
    />
  );
};
